import React from 'react';
import { Brain, Sparkles, Star } from 'lucide-react';

interface Learning {
  id: string;
  learning_type: string;
  insight: string;
  confidence: number;
  is_manual: boolean;
  data_points: number;
}

interface LearningContextBoxProps {
  learnings: Learning[];
  isLoading?: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  performance: '📊 Performance',
  prompt: '✏️ Prompt',
  approval: '✅ Freigabe',
  brand: '🎨 Brand',
  audience: '👥 Zielgruppe',
  platform: '📱 Plattform',
  campaign: '📢 Kampagne',
  idea: '💡 Idee',
  feedback: '💬 Feedback',
};

export const LearningContextBox: React.FC<LearningContextBoxProps> = ({ learnings, isLoading }) => {
  if (isLoading) {
    return (
      <div style={{
        padding: 'var(--space-4)', borderRadius: 12,
        background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
      }}>
        <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
          <Brain size={14} style={{ color: '#8B5CF6' }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Learning-Kontext wird geladen...</span>
        </div>
        <div className="skeleton" style={{ height: 40 }} />
      </div>
    );
  }

  if (learnings.length === 0) {
    return (
      <div style={{
        padding: 'var(--space-4)', borderRadius: 12,
        background: 'rgba(139,92,246,0.04)', border: '1px dashed rgba(139,92,246,0.15)',
      }}>
        <div className="flex items-center gap-2">
          <Brain size={14} style={{ color: '#8B5CF680' }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Noch keine Erfahrungswerte. Die KI lernt mit jedem Post dazu.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: 'var(--space-4)', borderRadius: 12,
      background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
    }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-3)' }}>
        <div className="flex items-center gap-2">
          <Brain size={14} style={{ color: '#8B5CF6' }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#8B5CF6' }}>
            Learning-Kontext ({learnings.length} Erfahrungswerte)
          </span>
        </div>
        <Sparkles size={12} style={{ color: '#8B5CF680' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {learnings.slice(0, 5).map((l) => (
          <div key={l.id} className="flex items-start gap-2" style={{ fontSize: 11, lineHeight: 1.4 }}>
            <span style={{ flexShrink: 0 }}>
              {l.is_manual ? <Star size={10} style={{ color: '#F59E0B', marginTop: 2 }} /> : '•'}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>
                {TYPE_LABELS[l.learning_type] || l.learning_type}:
              </span>{' '}
              {l.insight}
              <span style={{ opacity: 0.6, marginLeft: 4 }}>
                ({Math.round(l.confidence * 100)}%)
              </span>
            </span>
          </div>
        ))}
        {learnings.length > 5 && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)', paddingLeft: 16 }}>
            +{learnings.length - 5} weitere Learnings werden im Prompt berücksichtigt
          </span>
        )}
      </div>
    </div>
  );
};
